import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Target, DollarSign, Trophy, Copy, X, PartyPopper, ArrowRight, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { ensureAnonUser } from "@/lib/anonSession";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AtinaleUserPage() {
  const nav = useNavigate();
  const [phase, setPhase] = useState("list"); // list | play | result
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [guess, setGuess] = useState("");
  const [userName, setUserName] = useState(() => localStorage.getItem("holaradar_user_name") || "");
  const [result, setResult] = useState(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        await ensureAnonUser();
        let lat, lng;
        try {
          const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: 8000 }));
          lat = pos.coords.latitude; lng = pos.coords.longitude;
        } catch { /* GPS opcional */ }
        const url = `${API}/api/pwa/atinale/challenges` + (lat ? `?lat=${lat}&lng=${lng}` : "");
        const { data } = await axios.get(url);
        setChallenges(data.challenges || []);
      } catch { toast.error("No pude cargar los retos"); }
      finally { setLoading(false); }
    })();
  }, []);

  const pickChallenge = (c) => { setSelected(c); setGuess(""); setResult(null); setPhase("play"); };

  const submit = async (e) => {
    e.preventDefault();
    if (userName.trim().length < 3) return toast.error("Escribe tu nombre completo");
    if (!guess || parseFloat(guess) <= 0) return toast.error("Escribe un precio válido");
    setPlaying(true);
    try {
      const device_id = localStorage.getItem("holaradar_device_id");
      localStorage.setItem("holaradar_user_name", userName.trim());
      const { data } = await axios.post(`${API}/api/pwa/atinale/guess`, {
        device_id,
        merchant_id: selected.merchant_id,
        user_name: userName.trim(),
        guess: parseFloat(guess),
      });
      setResult(data);
      setPhase("result");
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setPlaying(false); }
  };

  const copyCode = async () => {
    if (!result?.prize_code) return;
    try { await navigator.clipboard.writeText(result.prize_code); toast.success("Copiado"); } catch { toast.info("Copia manualmente"); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1120] via-neutral-950 to-black text-white">
      <header className="border-b border-white/10 bg-black/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#FF007A] to-[#0055FF] grid place-items-center">
              <Target className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">HOLA RADAR</div>
              <div className="font-heading font-extrabold text-base">Atínale al Precio</div>
            </div>
          </div>
          <button onClick={() => nav("/")} className="text-neutral-400 hover:text-white p-2"><X className="w-5 h-5" /></button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-4 md:p-8">
        {phase === "list" && (
          loading ? <div className="text-center py-12 text-neutral-400">Cargando retos…</div> :
          challenges.length === 0 ? (
            <div className="text-center py-12">
              <Target className="w-12 h-12 mx-auto text-neutral-600 mb-3" />
              <div className="font-heading font-extrabold text-lg">Aún no hay retos activos</div>
              <p className="text-sm text-neutral-400 mt-1">Vuelve pronto — los comercios están subiendo sus retos.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <h2 className="font-heading font-extrabold text-2xl">Retos activos</h2>
                <p className="text-sm text-neutral-400 mt-1">Adivina el precio exacto y llévate el producto <strong className="text-[#FF007A]">GRATIS</strong>. Solo 1 intento por reto.</p>
              </div>
              {challenges.map((c) => (
                <button key={c.challenge_id} onClick={() => pickChallenge(c)} className="w-full text-left rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-[#FF007A]/40 transition p-4 flex gap-3" data-testid={`atinale-card-${c.merchant_id}`}>
                  <div className="text-[10px] uppercase tracking-widest text-[#FF007A] font-bold shrink-0">
                    <Store className="w-3 h-3 inline" /> {c.business_name}
                  </div>
                  <div className="flex-1"></div>
                  <ArrowRight className="w-4 h-4 text-neutral-500 shrink-0 self-center" />
                </button>
              ))}
            </div>
          )
        )}

        {phase === "play" && selected && (
          <div className="space-y-5">
            <button onClick={() => setPhase("list")} className="text-sm text-neutral-400 hover:text-white">← Volver</button>
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold flex items-center gap-1"><Store className="w-3 h-3" /> {selected.business_name}</div>
              <h2 className="font-heading font-extrabold text-2xl mt-1">{selected.product_name}</h2>
            </div>
            <div className="rounded-2xl overflow-hidden bg-white/5 border border-white/10">
              <img src={selected.image} alt={selected.product_name} className="w-full aspect-square object-cover" />
            </div>
            <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-4">
              <div>
                <Label className="text-xs text-neutral-400">Tu nombre completo</Label>
                <Input value={userName} onChange={(e) => setUserName(e.target.value)} placeholder="Ej: Ana López" className="mt-1 h-11 rounded-xl bg-white/10 border-white/20 text-white" required minLength={3} maxLength={120} data-testid="atinale-user-name" />
              </div>
              <div>
                <Label className="text-xs text-neutral-400">¿Cuánto crees que cuesta esta prenda o servicio?</Label>
                <div className="flex items-center gap-2 mt-1">
                  <DollarSign className="w-5 h-5 text-neutral-400" />
                  <Input type="number" step="0.01" min={1} value={guess} onChange={(e) => setGuess(e.target.value)} placeholder="450.00 MXN" className="h-12 rounded-xl bg-white/10 border-white/20 text-white text-lg" required data-testid="atinale-guess-input" />
                </div>
                <p className="text-[10px] text-neutral-500 mt-1">Tienes hasta 2 intentos — piensa bien tu tiro. Solo el precio exacto gana.</p>
              </div>
              <Button type="submit" disabled={playing} className="w-full h-12 rounded-full bg-[#FF007A] hover:bg-[#e60070] font-bold" data-testid="atinale-submit-guess">
                <Target className="w-4 h-4" /> {playing ? "Verificando…" : "Enviar mi tiro"}
              </Button>
            </form>
          </div>
        )}

        {phase === "result" && result && (
          <div className="space-y-5">
            {result.won ? (
              <div className="rounded-3xl bg-gradient-to-br from-[#FF007A] to-[#0055FF] p-8 text-center shadow-2xl relative overflow-hidden" data-testid="atinale-win">
                <div className="absolute -right-6 -top-6 opacity-20"><PartyPopper className="w-40 h-40" /></div>
                <PartyPopper className="w-12 h-12 mx-auto mb-2" />
                <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">¡Le atinaste al precio exacto!</div>
                <h2 className="font-heading font-extrabold text-3xl md:text-4xl mt-2 leading-tight">¡Ganaste!</h2>
                <p className="text-sm mt-2 opacity-95">Pasa a <strong>{result.business_name}</strong> por tu <strong>{result.product_name}</strong> GRATIS.</p>
                <div className="mt-5 rounded-2xl bg-white/15 backdrop-blur p-4">
                  <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">Tu código de premio</div>
                  <div className="font-heading font-extrabold text-3xl md:text-4xl mt-1 font-mono tracking-wider" data-testid="atinale-prize-code">
                    {result.prize_code}
                  </div>
                  <button onClick={copyCode} className="mt-3 text-xs bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-full inline-flex items-center gap-1">
                    <Copy className="w-3 h-3" /> Copiar código
                  </button>
                </div>
                <p className="text-xs opacity-80 mt-4">Muestra este código al staff cuando llegues. ¡No lo pierdas!</p>
              </div>
            ) : result.attempts_remaining > 0 ? (
              <div className="rounded-3xl bg-amber-500/10 border-2 border-amber-500/40 p-8 text-center" data-testid="atinale-retry">
                <div className="text-5xl mb-3">🎯</div>
                <h2 className="font-heading font-extrabold text-2xl">Estuviste cerca</h2>
                <p className="text-sm text-neutral-300 mt-2">Te queda <strong className="text-amber-300">1 intento más</strong>. Piensa bien tu tiro.</p>
                <Button
                  onClick={() => { setResult(null); setGuess(""); setPhase("play"); }}
                  className="mt-5 h-11 rounded-full bg-amber-500 hover:bg-amber-600 font-bold px-8"
                  data-testid="atinale-try-again"
                >
                  <Target className="w-4 h-4" /> Intentar de nuevo
                </Button>
              </div>
            ) : (
              <div className="rounded-3xl bg-white/5 border border-white/10 p-8 text-center" data-testid="atinale-lose">
                <div className="text-6xl mb-3">🍀</div>
                <h2 className="font-heading font-extrabold text-2xl">Suerte para la otra</h2>
                <p className="text-sm text-neutral-400 mt-2 leading-relaxed">
                  {result.already_guessed ? result.message : "Ya usaste tus 2 intentos en este reto. Prueba en otros retos de la lista."}
                </p>
              </div>
            )}
            <Button onClick={() => setPhase("list")} variant="outline" className="w-full h-11 rounded-full border-white/20 text-white bg-white/5 hover:bg-white/10">
              Ver más retos
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
